<template>
  <div>
    <div class="d-flex kr-title-wrapper" v-if="parent">
      <initiative-icon
        class="parent-initiative-icon"
        :is-done-and-closed="parent.isDoneAndClosed"
        v-if="isTask"
      />
      <i class="kr-icon flex-shrink-0" v-else></i>
      <router-link
        :to="parentLocation"
        tabindex="-1"
        class="text-medium text-secondary kr-title"
        :title="parent.title"
        :id="'parent-' + uuid"
        v-no-capture
      >
        {{ parent.title }}
      </router-link>
    </div>
    <entity-popover
      :target="'parent-' + uuid"
      :entity-type="isTask ? ENTITY_TYPE.INITIATIVE : ENTITY_TYPE.KEY_RESULT"
      :org-id="currentInitiative.organizationId"
      :goal-id="currentInitiative.goalId"
      :kr-id="currentInitiative.keyResultId"
    />
    <initiative-row
      :key="currentInitiative.id"
      :orgId="currentInitiative.organizationId"
      :krId="currentInitiative.keyResultId"
      :goalId="currentInitiative.goalId"
      :initiative="currentInitiative"
      :edit-on-load="editInitiativeOnLoad"
      view-type="details"
      :draggable="false"
      :expand-on-load="true"
      :can-toggle-expansion="false"
      :hide-comment-icon="true"
      :auto-show-action-icons="true"
      :selectable="false"
      :selectable-tasks="false"
      @update-initiative="onInitiativeUpdate"
      @delete-initiatives="$emit('delete-initiatives', $event)"
    ></initiative-row>
    <div class="activity" :class="{ 'top-border': !enableTasksFeature }">
      <div class="sub-heading d-flex align-items-center">
        <i class="comment-icon"></i>
        <feeds-heading
          class="ml-2 text-medium"
          heading="Initiative Activity"
          :entity-type="ENTITY_TYPE.INITIATIVE"
          :selected-filter="selectedFilter"
          @filter="useDefaultActivityFilter = false"
        />
      </div>
      <initiative-discussion
        :query="query"
        :focus-add-comment-on-load="focusAddCommentOnLoad"
        :initiative="currentInitiative"
        :selected-activity-filter="selectedFilter"
      />
    </div>
  </div>
</template>

<script>
import EntityPopover from '@/components/EntityPopover'
import FeedsHeading from '@/components/FeedsHeading'
import InitiativeIcon from '@/components/initiative/InitiativeIcon'
import InitiativeDiscussion from '@/components/InitiativeDiscussion'
import { ENTITY_TYPE } from '@/constants/entities'
import { featureFlags } from '@/constants/featureFlags'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import { getInitiativePageRouteConfig, getKrPageRouteConfig } from '@/utils/navigationConfigs'
import { mapActions } from 'vuex'

/**
 * This is the details view of an initiative. It shows initiative details and activities
 * @emits:
 * - delete-initiative
 */
export default {
  name: 'InitiativeContent',
  props: [
    'initiative',
    'editInitiativeOnLoad',
    'focusAddCommentOnLoad',
    'defaultActivityFilter',
    'query'
  ],
  components: {
    InitiativeRow: () => import('@/components/initiative/InitiativeRow'),
    InitiativeDiscussion,
    EntityPopover,
    FeedsHeading,
    InitiativeIcon
  },
  mixins: [FeaturesMixin, ComponentIdMixin],
  data() {
    return {
      ENTITY_TYPE,
      isTask: !!this.initiative.parentId,
      currentInitiative: this.initiative,
      enableTasksFeature: this.getFeatureFlagValue(
        this.initiative.organizationId,
        featureFlags.ALLOW_TASKS
      ),
      useDefaultActivityFilter: true
    }
  },
  computed: {
    kr() {
      return this.$store.getters.keyResultById(this.initiative.keyResultId)
    },
    parent() {
      return this.isTask ? this.$store.getters.initiativeById(this.initiative.parentId) : this.kr
    },
    parentLocation() {
      return this.isTask ? this.parentInitiativePageLocation : this.krPageLocation
    },
    goal() {
      return this.$store.getters.goalById(this.kr.goalId)
    },
    selectedFilter() {
      return this.defaultActivityFilter && this.useDefaultActivityFilter
        ? this.defaultActivityFilter
        : this.$store.getters['feedPreference/selectedFilter'](ENTITY_TYPE.INITIATIVE)
    },
    krPageLocation() {
      return getKrPageRouteConfig({
        krId: this.kr.id,
        orgId: this.initiative.organizationId
      })
    },
    parentInitiativePageLocation() {
      return getInitiativePageRouteConfig({
        initiativeId: this.initiative.parentId,
        orgId: this.initiative.organizationId
      })
    }
  },
  methods: {
    ...mapActions(['getInitiative', 'getKRAlone']),
    refreshKR() {
      if (this.initiative.keyResultId && !this.kr) {
        return this.getKRAlone({
          orgId: this.initiative.organizationId,
          goalId: this.initiative.goalId,
          krId: this.initiative.keyResultId
        })
      }
    },
    refreshParentInitiative() {
      if (!this.parent) {
        return this.getInitiative({
          orgId: this.initiative.organizationId,
          initiativeId: this.initiative.parentId
        })
      }
    },
    onLoad() {
      if (this.isTask) {
        this.refreshParentInitiative()
      } else {
        this.refreshKR()
      }
    },
    onInitiativeUpdate(initiative) {
      this.currentInitiative = initiative
    }
  },
  mounted() {
    this.onLoad()
  },
  watch: {
    initiative(newValue) {
      this.currentInitiative = newValue
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.initiative-row /deep/ .cell {
  border-top: none;
}

/deep/ .note-wrapper {
  background-color: $documentColor;
}

.comment-icon {
  @include dimension(14px, 14px);
  background-size: contain;
}

.activity {
  margin-left: 24px;
  margin-right: 28px;
  padding-left: 4px;
}

.sub-heading {
  padding-top: 14px;
  padding-bottom: 14px;
}

.kr-title-wrapper {
  margin-left: 27px;
  margin-bottom: 8px;
}

.kr-title {
  line-height: 0.875rem;
}

.kr-icon {
  margin-left: 6px;
  margin-right: 10px;
}

.parent-initiative-icon {
  margin: -4px 4px 0 2px;
}

/deep/ .initiative-options .edit-menu {
  padding-top: 12px;
}

.top-border {
  border-top: 1px solid $borderPrimary;
}

@include media-breakpoint-down(md) {
  .activity {
    margin: 0;
  }
}
</style>
